import { defineStore } from 'pinia';
import { ref } from 'vue';

export type ToastType = 'error' | 'success' | 'warning' | 'info';

export interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration: number; // in milliseconds, 0 means persistent
  createdAt: number;
  timer?: ReturnType<typeof setTimeout>;
  pausedAt?: number;
  remainingTime: number;
}

export interface ToastOptions {
  type?: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

export const useToastStore = defineStore('toast', () => {
  const toasts = ref<ToastItem[]>([]);

  const remove = (id: string): void => {
    const index = toasts.value.findIndex((t) => t.id === id);
    if (index !== -1) {
      const toast = toasts.value[index];
      if (toast?.timer) clearTimeout(toast.timer);
      toasts.value.splice(index, 1);
    }
  };

  const show = (options: ToastOptions): string => {
    const id = `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const duration = options.duration ?? (options.type === 'error' ? 5000 : 4000);
    const type = options.type ?? 'info';

    const toastItem: ToastItem = {
      id,
      type,
      title: options.title,
      message: options.message,
      duration,
      createdAt: Date.now(),
      remainingTime: duration,
    };

    if (duration > 0) {
      toastItem.timer = setTimeout(() => {
        remove(id);
      }, duration);
    }

    // Limit to max 5 simultaneous toasts to avoid screen clutter
    if (toasts.value.length >= 5) {
      const oldest = toasts.value[0];
      if (oldest) remove(oldest.id);
    }

    toasts.value.push(toastItem);
    return id;
  };

  const pause = (id: string): void => {
    const toast = toasts.value.find((t) => t.id === id);
    if (!toast || toast.duration <= 0 || !toast.timer) return;

    clearTimeout(toast.timer);
    toast.timer = undefined;
    const elapsed = Date.now() - (toast.pausedAt ? toast.pausedAt : toast.createdAt);
    toast.remainingTime = Math.max(500, toast.remainingTime - elapsed);
    toast.pausedAt = Date.now();
  };

  const resume = (id: string): void => {
    const toast = toasts.value.find((t) => t.id === id);
    if (!toast || toast.duration <= 0 || toast.timer) return;

    toast.pausedAt = undefined;
    toast.timer = setTimeout(() => {
      remove(id);
    }, toast.remainingTime);
  };

  const error = (message: string, options?: Omit<ToastOptions, 'type' | 'message'>): string =>
    show({
      type: 'error',
      message,
      title: options?.title ?? 'Terjadi Kesalahan',
      duration: options?.duration ?? 5000,
    });

  const success = (message: string, options?: Omit<ToastOptions, 'type' | 'message'>): string =>
    show({
      type: 'success',
      message,
      title: options?.title ?? 'Berhasil',
      duration: options?.duration ?? 4000,
    });

  const warning = (message: string, options?: Omit<ToastOptions, 'type' | 'message'>): string =>
    show({
      type: 'warning',
      message,
      title: options?.title ?? 'Perhatian',
      duration: options?.duration ?? 4500,
    });

  const info = (message: string, options?: Omit<ToastOptions, 'type' | 'message'>): string =>
    show({
      type: 'info',
      message,
      title: options?.title ?? 'Informasi',
      duration: options?.duration ?? 4000,
    });

  const clear = (): void => {
    toasts.value.forEach((t) => {
      if (t.timer) clearTimeout(t.timer);
    });
    toasts.value = [];
  };

  return {
    toasts,
    show,
    error,
    success,
    warning,
    info,
    remove,
    pause,
    resume,
    clear,
  };
});
